'use strict';
const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
function readingSize(text) { return text.replace(/^[ \t]{0,3}(`{3,}|~{3,})[ \t]*qomo-ui[ \t]*\r?\n[\s\S]*?(?:\r?\n[ \t]{0,3}\1[ \t]*(?=\r?\n|$)|$)/gim, '').length; }
function heldEnd(text, held, cursor) {
  const close = new RegExp('^[ \\t]{0,3}' + held.marker + '{' + held.length + ',}[ \\t]*\\r?(?=\\n|$)', 'gm');
  close.lastIndex = cursor;
  const match = close.exec(text);
  return match ? close.lastIndex : -1;
}
function graphemeEnd(text, start, budget, ended) {
  const tail = text.slice(start, Math.min(text.length, start + budget + 64));
  let size = 0, lastStart = start;
  for (const item of segmenter ? segmenter.segment(tail) : Array.from(tail).map(segment => ({ segment }))) {
    const next = size + item.segment.length;
    if (next > budget && size) break;
    lastStart = start + size; size = next; if (size >= budget) break;
  }
  let end = start + size;
  // A high surrogate / unfinished emoji cluster may span upstream deltas.
  if (!ended && end === text.length) end = lastStart;
  return end;
}
class StreamReveal {
  constructor() { this.text = ''; this.cursor = 0; this.last = null; this.credit = 0; this.deadline = null; this.held = null; this.rate = 72; }
  push(text) { this.text += text; }
  readingRemaining() {
    const close = this.held ? heldEnd(this.text, this.held, this.cursor) : -1;
    return readingSize(this.held ? (close < 0 ? '' : this.text.slice(close)) : this.text.slice(this.cursor));
  }
  end(now) { if (this.deadline === null) this.deadline = now + Math.min(2400, Math.max(260, this.readingRemaining() * 9)); }
  read(now, { immediate = false, force = false } = {}) {
    const previous = this.cursor, remaining = this.text.length - previous, ended = this.deadline !== null;
    let blocked = false;
    if (force) { this.cursor = this.text.length; this.held = null; }
    else {
      const dt = this.last === null ? 1 / 60 : Math.min(.05, Math.max(0, now - this.last) / 1000);
      const readable = this.readingRemaining();
      const target = ended ? Math.max(72, readable * 1.15 / Math.max(.05, (this.deadline - now) / 1000)) : Math.max(72, readable / 1.25);
      // Ease into changing network speeds; a delayed packet must not become a burst.
      this.rate += (Math.min(480, target) - this.rate) * (1 - Math.exp(-dt / .16));
      this.credit = Math.min(remaining, this.credit + this.rate * dt);
      let budget = immediate ? remaining : Math.min(24, Math.floor(this.credit));
      while (budget > 0 && this.cursor < this.text.length) {
        if (this.held) {
          const end = heldEnd(this.text, this.held, this.cursor);
          if (end < 0 && !ended) { blocked = true; break; }
          const next = end < 0 ? this.text.length : end;
          budget -= Math.min(budget, next - this.cursor); this.cursor = next; this.held = null;
          if (!immediate) { budget = 0; this.credit = 0; }
          continue;
        }
        const rest = this.text.slice(this.cursor), lineStart = this.cursor === 0 || this.text[this.cursor - 1] === '\n';
        if (lineStart && /^[ \t]{1,3}$/.test(rest) && !ended) { blocked = true; break; }
        if (/^\n[ \t]{0,3}[`~]/.test(rest)) { this.cursor++; budget--; continue; }
        if (lineStart && /^[ \t]{0,3}[`~]/.test(rest)) {
          // Hold a partial fence header until its language and newline arrive.
          if (!rest.includes('\n') && rest.length < 100 && !ended) { blocked = true; break; }
          const header = rest.match(/^[ \t]{0,3}(`{3,}|~{3,})[ \t]*qomo-ui[ \t]*\r?\n/i);
          if (header) { this.cursor += header[0].length; budget = Math.max(0, budget - header[0].length); this.held = { marker: header[1][0], length: header[1].length }; continue; }
        }
        const mathOpen = rest.startsWith('\\[') ? ['\\[', '\\]'] : rest.startsWith('\\(') ? ['\\(', '\\)'] : rest.startsWith('$$') ? ['$$', '$$'] : null;
        if (mathOpen) {
          const end = this.text.indexOf(mathOpen[1], this.cursor + mathOpen[0].length);
          if (end < 0 && !ended) { blocked = true; break; }
          const next = end < 0 ? this.text.length : end + mathOpen[1].length;
          this.cursor = next; budget = Math.max(0, budget - (next - previous));
          if (!immediate) { budget = 0; this.credit = 0; }
          continue;
        }
        if (!ended && rest === '\\') { blocked = true; break; }
        // Stop the prose run before structural syntax so math and component JSON stay atomic.
        const nextMarker = rest.slice(1).search(/\\[\[(]|\$\$|\n[ \t]{0,3}[`~]/);
        const limit = nextMarker < 0 ? budget : Math.min(budget, nextMarker + 1 + (rest[nextMarker + 1] === '\n' ? 1 : 0));
        const end = graphemeEnd(this.text, this.cursor, Math.max(1, limit), ended);
        if (end <= this.cursor) { blocked = true; break; }
        budget -= end - this.cursor; this.cursor = end;
      }
      this.credit = Math.max(0, this.credit - (this.cursor - previous));
    }
    this.last = now;
    return { blocked, text: this.text.slice(previous, this.cursor), pending: this.cursor < this.text.length, waiting: this.cursor === previous && this.cursor < this.text.length, done: ended && this.cursor === this.text.length };
  }
}
module.exports = { StreamReveal };
